import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Download, User } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ContactQRGenerator() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [organization, setOrganization] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [address, setAddress] = useState('')
  const [qrValue, setQrValue] = useState('')

  const generateContactQR = () => {
    if (!firstName.trim() && !lastName.trim()) {
      alert('Please enter at least a first or last name')
      return
    }

    // Generate vCard format
    let vcard = 'BEGIN:VCARD\n'
    vcard += 'VERSION:3.0\n'
    
    if (firstName || lastName) {
      vcard += `FN:${firstName} ${lastName}\n`
      vcard += `N:${lastName};${firstName};;;\n`
    }
    
    if (organization) {
      vcard += `ORG:${organization}\n`
    }
    
    if (phone) {
      vcard += `TEL:${phone}\n`
    }
    
    if (email) {
      vcard += `EMAIL:${email}\n`
    }
    
    if (website) {
      const url = website.startsWith('http') ? website : `https://${website}`
      vcard += `URL:${url}\n`
    }
    
    if (address) {
      vcard += `ADR:;;${address};;;\n`
    }
    
    vcard += 'END:VCARD'
    
    setQrValue(vcard)
  }

  const downloadQR = () => {
    const svg = document.getElementById('contact-qr-svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      canvas.width = 300
      canvas.height = 300
      ctx.drawImage(img, 0, 0)
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `contact-${firstName || 'contact'}.png`
        link.click()
        URL.revokeObjectURL(url)
      })
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-3">First Name:</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="input w-full"
                  placeholder="John"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-3">Last Name:</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="input w-full"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Organization:</label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="input w-full"
                placeholder="Company Name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Phone:</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input w-full"
                  placeholder="+1234567890"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-3">Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input w-full"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Website:</label>
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="input w-full"
                placeholder="example.com"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Address:</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="input w-full"
                placeholder="123 Main St, City, State"
              />
            </div>

            <Button onClick={generateContactQR} variant="primary" className="w-full">
              <User className="w-5 h-5 mr-2" />
              Generate Contact QR Code
            </Button>
          </div>
        </Card.Body>
      </Card>

      {qrValue && (
        <Card>
          <Card.Body>
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <QRCodeSVG
                  id="contact-qr-svg"
                  value={qrValue}
                  size={300}
                  level="M"
                  includeMargin={true}
                />
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-left">
                <p className="text-sm text-gray-700 mb-1">
                  <strong>Name:</strong> {firstName} {lastName}
                </p>
                {organization && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>Organization:</strong> {organization}
                  </p>
                )}
                {phone && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>Phone:</strong> {phone}
                  </p>
                )}
                {email && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>Email:</strong> {email}
                  </p>
                )}
              </div>
              <Button onClick={downloadQR} variant="primary" size="lg" className="w-full">
                <Download className="w-5 h-5 mr-2" />
                Download Contact QR Code
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {!qrValue && (
        <Card>
          <Card.Body className="text-center py-12">
            <p className="text-gray-600">Fill in contact information and click "Generate Contact QR Code"</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

